// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {MediaDimensionSize} from '@app/features/accessibility/state/Accessibility';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {ExpiryFootnote} from '@app/features/app/components/shared/ExpiryFootnote';
import FavoriteGif from '@app/features/expressions/state/FavoriteGif';
import {Switch} from '@app/features/ui/components/form/Switch';
import {SwitchGroup, SwitchGroupItem} from '@app/features/ui/components/SwitchGroup';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/chat_settings_tab/MediaTab.module.css';
import {MS_PER_DAY} from '@app/vendor/date_utils/DateConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {DownloadSimpleIcon, ImageSquareIcon, StarIcon, TrashIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const MediaPreview = observer(
	({
		showFavorite,
		showDownload,
		showDelete,
		showSuppress,
		showGifIndicator,
		showExpiryIndicator,
	}: {
		showFavorite: boolean;
		showDownload: boolean;
		showDelete: boolean;
		showSuppress: boolean;
		showGifIndicator: boolean;
		showExpiryIndicator: boolean;
	}) => {
		const {t} = useLingui();
		const previewExpiry = useMemo(() => new Date(Date.now() + 12 * MS_PER_DAY), []);
		return (
			<div className={styles.previewContainer}>
				<div className={styles.previewWrapper}>
					<div className={styles.previewBox}>
						<ImageSquareIcon className={styles.previewIcon} />
						{showGifIndicator && <div className={styles.gifIndicator}>GIF</div>}
						<div className={styles.actionButtons}>
							{showDelete && (
								<button type="button" className={styles.actionButton} aria-label={t`Delete media`}>
									<TrashIcon size={18} weight="bold" className={styles.actionButtonIcon} />
								</button>
							)}
							{showDownload && (
								<button type="button" className={styles.actionButton} aria-label={t`Download media`}>
									<DownloadSimpleIcon size={18} weight="bold" className={styles.actionButtonIcon} />
								</button>
							)}
							{showFavorite && (
								<button type="button" className={styles.actionButton} aria-label={t`Add media to favorites`}>
									<StarIcon size={18} weight="bold" className={styles.actionButtonIcon} />
								</button>
							)}
						</div>
					</div>
					{showSuppress && (
						<button type="button" className={styles.suppressButton} aria-label={t`Hide media preview`}>
							<XIcon size={16} weight="bold" />
						</button>
					)}
					{showExpiryIndicator && (
						<ExpiryFootnote expiresAt={previewExpiry} isExpired={false} className={styles.expiryFootnotePreview} />
					)}
				</div>
			</div>
		);
	},
);
export const MediaTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const mobileLayout = MobileLayout;
	const {
		showMediaFavoriteButton,
		showMediaDownloadButton,
		showMediaDeleteButton,
		showSuppressEmbedsButton,
		showGifIndicator,
		showAttachmentExpiryIndicator,
		autoSendKlipyGifs,
		embedMediaDimensionSize,
		attachmentMediaDimensionSize,
	} = Accessibility;
	const mediaSizeOptions = useMemo(
		(): ReadonlyArray<RadioOption<MediaDimensionSize>> => [
			{
				value: MediaDimensionSize.SMALL,
				name: t`Compact (400x300)`,
				desc: t`Smaller media size`,
			},
			{
				value: MediaDimensionSize.LARGE,
				name: t`Comfortable (550x400)`,
				desc: t`Larger media size with more detail`,
			},
		],
		[t],
	);
	return (
		<>
			<SettingsTabSection
				title={t`Media Size Preferences`}
				description={t`Customize the maximum display size for embedded and attached media. Smaller sizes use less screen space, while larger sizes show more detail.`}
			>
				<div className={styles.radioSections}>
					<div className={styles.radioSection}>
						<div className={styles.radioLabelContainer}>
							<div className={styles.radioLabel}>
								<Trans>Media from links (embeds)</Trans>
							</div>
						</div>
						<RadioGroup
							options={mediaSizeOptions}
							value={embedMediaDimensionSize}
							onChange={(value) => AccessibilityCommands.update({embedMediaDimensionSize: value})}
							aria-label={t`Select media size for embedded content from links`}
						/>
					</div>
					<div className={styles.radioSection}>
						<div className={styles.radioLabelContainer}>
							<div className={styles.radioLabel}>
								<Trans>Uploaded attachments</Trans>
							</div>
						</div>
						<RadioGroup
							options={mediaSizeOptions}
							value={attachmentMediaDimensionSize}
							onChange={(value) => AccessibilityCommands.update({attachmentMediaDimensionSize: value})}
							aria-label={t`Select media size for uploaded attachments`}
						/>
					</div>
				</div>
			</SettingsTabSection>
			<SettingsTabSection title={t`GIF Behavior`} description={t`Control how GIFs are inserted into chat`}>
				<div className={styles.sectionContent}>
					<Switch
						label={t`Automatically send GIFs when selected`}
						value={autoSendKlipyGifs}
						onChange={(value) => AccessibilityCommands.update({autoSendKlipyGifs: value})}
					/>
					<Switch
						label={t`Save GIF favorites as Saved Media`}
						description={t`When on, starring a GIF uploads it to your Saved Media so it's tagged, searchable, and persistent — but it counts against your saved media limit. When off (default), starred GIFs are stored as URL-only favorites synced across your devices; the original media may disappear if its host removes it.`}
						value={FavoriteGif.saveGifFavoritesAsSavedMedia}
						onChange={(value) => FavoriteGif.setSaveGifFavoritesAsSavedMedia(value)}
					/>
				</div>
			</SettingsTabSection>
			{!mobileLayout.enabled && (
				<SettingsTabSection
					title={t`Media Buttons`}
					description={t`Customize which buttons appear on media attachments and embeds when hovering over messages.`}
				>
					<div className={styles.sectionContent}>
						<MediaPreview
							showFavorite={showMediaFavoriteButton}
							showDownload={showMediaDownloadButton}
							showDelete={showMediaDeleteButton}
							showSuppress={showSuppressEmbedsButton}
							showGifIndicator={showGifIndicator}
							showExpiryIndicator={showAttachmentExpiryIndicator}
						/>
						<SwitchGroup>
							<SwitchGroupItem
								label={t`Show GIF Indicator`}
								value={showGifIndicator}
								onChange={(value) => AccessibilityCommands.update({showGifIndicator: value})}
							/>
							<SwitchGroupItem
								label={t`Show attachment expiry indicator`}
								value={showAttachmentExpiryIndicator}
								onChange={(value) => AccessibilityCommands.update({showAttachmentExpiryIndicator: value})}
							/>
							<SwitchGroupItem
								label={t`Show Delete Button`}
								value={showMediaDeleteButton}
								onChange={(value) => AccessibilityCommands.update({showMediaDeleteButton: value})}
							/>
							<SwitchGroupItem
								label={t`Show Download Button`}
								value={showMediaDownloadButton}
								onChange={(value) => AccessibilityCommands.update({showMediaDownloadButton: value})}
							/>
							<SwitchGroupItem
								label={t`Show Favorite Button`}
								value={showMediaFavoriteButton}
								onChange={(value) => AccessibilityCommands.update({showMediaFavoriteButton: value})}
							/>
							<SwitchGroupItem
								label={t`Show suppress embeds button`}
								value={showSuppressEmbedsButton}
								onChange={(value) => AccessibilityCommands.update({showSuppressEmbedsButton: value})}
							/>
						</SwitchGroup>
					</div>
				</SettingsTabSection>
			)}
		</>
	);
});
